import { TextField } from 'components/TextField';
import map from 'lodash/map';
import includes from 'lodash/includes';
import { ChangeEvent, useMemo, useState } from 'react';
import filter from 'lodash/filter';
import { deburr } from 'lodash';
import { EmployerJobListingResource } from 'util/resources/EmployerJobListing';
import { LocationResource } from 'util/resources/Location';
import { Select, SelectOption, SelectOptionProps } from 'components/Select';
import { RadioGroup } from '@headlessui/react';
import { Radio } from 'components/Radio';
import sortBy from 'lodash/sortBy';

const OTHER_OPTION = {
  id: 'other',
  text: 'Other',
  value: 'other',
};

type SelectJobListingsProps = {
  /**
   * Array of employer job listings
   * @required
   */
  jobListings: EmployerJobListingResource[];

  /**
   * The employer locations in case the school selects "Other"
   * @required
   */
  locations: LocationResource[];

  /**
   * The selected employer job
   * @default []
   */
  selectedJobListing?: EmployerJobListingResource;

  /**
   * The function to handle selection change
   * @required
   */
  onSelectionChange: (value: string) => void;

  /**
   * The callback for clearing the selection
   * @required
   */
  onClearSelection?: () => void;

  /**
   * The selected employer location
   * @default undefined
   */
  selectedLocation?: LocationResource;

  /**
   * The function to handle location change
   * @default undefined
   */
  onLocationChange?: (value: string) => void;

  /**
   * The name of a non-employer job
   * @default ''
   */
  otherValue?: string;

  /**
   * The function to handle non-employer job change
   * @default () => {}
   */
  onOtherChange?: (name?: string) => void;
};

export const SelectJobListings = (props: SelectJobListingsProps) => {
  const {
    jobListings,
    locations,
    selectedJobListing,
    onSelectionChange,
    selectedLocation,
    onLocationChange,
    otherValue,
    onOtherChange,
  } = props;

  const jobListingsPrimitive = map(jobListings, 'id').join(',');

  const [searchValue, setSearchValue] = useState('');
  const [isOtherSelected, setIsOtherSelected] = useState(false);

  const jobListingOptions = useMemo(
    () =>
      sortBy(
        map(jobListings, (job) => ({
          id: job.id,
          value: job.id,
          text: job.title,
          subText: job.getLocationText(true),
        })),
        (option) => deburr(option.text),
      ),
    [jobListingsPrimitive],
  );

  const filteredOptions = useMemo(
    () =>
      !searchValue
        ? jobListingOptions
        : filter(jobListingOptions, (item) => {
            const title = item.text;
            return (
              includes(deburr(title.toLowerCase()), deburr(searchValue.toLowerCase())) ||
              includes(deburr(item.subText.toLowerCase()), deburr(searchValue.toLowerCase()))
            );
          }),
    [jobListingsPrimitive, searchValue],
  );

  const locationOptions: SelectOptionProps[] = useMemo(
    () =>
      locations.map((location) => ({
        id: location.id,
        value: location.id,
        children: location.name,
        description: `${location.address}, ${location.neighborhood}, ${location.zip}`,
      })),
    [locations],
  );

  const selectedLocationOption = useMemo(() => {
    if (!selectedLocation?.id) {
      return null;
    }

    const option = locationOptions.find((location) => location.id === selectedLocation.id);

    if (!option) {
      return null;
    }

    return option;
  }, [selectedLocation?.id]);

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setSearchValue(value);
  };

  const handleSelectionChange = (value: string) => {
    if (value === OTHER_OPTION.value) {
      setIsOtherSelected(true);
    } else {
      setIsOtherSelected(false);
    }

    onSelectionChange(value);
  };

  const handleOtherTextFieldChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    onOtherChange(value);
  };

  return (
    <div className="flex flex-col gap-4">
      {(!isOtherSelected || null) && (
        <div className="flex-none">
          <TextField
            placeholder="Search by title or address"
            value={searchValue}
            onChange={handleSearchChange}
          />
        </div>
      )}

      <RadioGroup
        className="flex flex-col gap-4"
        onChange={handleSelectionChange}
        value={isOtherSelected ? 'other' : selectedJobListing?.id || null}
      >
        {(!isOtherSelected || null) && (
          <div className="flex flex-col gap-3 sm:max-h-[268px] overflow-y-auto">
            {!filteredOptions?.length ? (
              <p className="text-sm text-neutral-darker">No jobs found</p>
            ) : (
              map(filteredOptions, (option) => (
                <div key={option.id} className="flex-none">
                  <RadioGroup.Option value={option.value}>
                    {({ checked }) => (
                      <Radio checked={checked}>
                        <p className="text-sm font-medium text-neutral-ink-blue">{option.text}</p>
                        {option.subText && (
                          <p className="text-xs text-neutral-dark">{option.subText}</p>
                        )}
                      </Radio>
                    )}
                  </RadioGroup.Option>
                </div>
              ))
            )}
          </div>
        )}

        <div className="flex-none flex flex-col gap-2">
          {(isOtherSelected || null) && (
            <p className="mb-4 text-neutral-dark">
              Please select the role that you hired this person for:
            </p>
          )}

          <div className="flex gap-1 justify-between items-center">
            <RadioGroup.Option value={OTHER_OPTION.value}>
              {({ checked }) => (
                <Radio checked={checked}>
                  <p className="text-sm font-medium text-neutral-ink-blue">{OTHER_OPTION.text}</p>
                </Radio>
              )}
            </RadioGroup.Option>

            {(isOtherSelected || null) && (
              <button
                className="text-xs underline cursor-pointer underline-offset-2 text-primary-poppy-red"
                onClick={() => {
                  handleSelectionChange(undefined);
                }}
              >
                Cancel
              </button>
            )}
          </div>
        </div>
      </RadioGroup>

      {(isOtherSelected || null) && (
        <div className="flex flex-col gap-3">
          <TextField
            placeholder="Type the role title"
            value={otherValue}
            onChange={handleOtherTextFieldChange}
          />

          {(locations?.length || null) && (
            <Select
              selectedOption={selectedLocationOption}
              onChange={onLocationChange}
              fullWidth
              placeholder="Select location"
            >
              {map(locationOptions, (location) => (
                <SelectOption
                  key={location.id}
                  value={location.value}
                  description={location.description}
                >
                  {location.children}
                </SelectOption>
              ))}
            </Select>
          )}
        </div>
      )}
    </div>
  );
};

SelectJobListings.displayName = 'SelectJobListings';
